import { type FC } from 'react';
import { Layer, Source } from 'react-map-gl/maplibre';
import { useSelector } from 'react-redux';

import { useThreeDController } from '@/features/3d/3DControllerProvider';
import { useBoundaryContext } from '@/features/boundaries/provider';
import { AcresMapboxSourceIds } from '@/mapbox-layers';
import { threeDBuildingsLayer } from '@/mapbox-layers/3d-buildings';
import { boundaryFillLayer } from '@/mapbox-layers/boundary';
import { boundaryOutlineLayer } from '@/mapbox-layers/boundary/boundary-outline';
import {
  activeFsaLayer,
  fsaFillLayer,
  fsaLabelsLayer,
} from '@/mapbox-layers/clu';
import { countyFillLayer } from '@/mapbox-layers/county/county';
import { countyLabelsLayer } from '@/mapbox-layers/county/county-label';
import { countyOutlineLayer } from '@/mapbox-layers/county/county-outline';
import {
  parcelFillLayer,
  visibleOwnerLabelsLayer,
} from '@/mapbox-layers/parcels/parcels';
import parcelsOutlineHover, {
  parcelsOutline,
} from '@/mapbox-layers/parcels/parcels-outline';
import {
  selectionDotLayer,
  selectionFillLayer,
  selectionLineLayer,
} from '@/mapbox-layers/selections';
import {
  stateFillLayer,
  stateHighlightLayer,
  stateLabelsLayer,
  stateOutlineLayer,
  stateOutlinePermanentLayer,
} from '@/mapbox-layers/states';
import {
  sectionLabelLayer,
  sectionLineLayer,
  sectionOutlineLayer,
  sectionOutlinePermanentLayer,
  sectionsFillLayer,
} from '@/mapbox-layers/trs/sections';
import {
  townshipOutlineLayer,
  townshipOutlinePermanentLayer,
} from '@/mapbox-layers/trs/township-outline';
import {
  townshipFillLayer,
  townshipLabelLayer,
  townshipLineLayer,
} from '@/mapbox-layers/trs/townships';
import { isCompFeature } from '@/models/features/guards';
import { useMapboxContext } from '@/providers/mapbox/MapboxProvider';
import { RootState } from '@/redux/store';

export const AllMapboxLayers: FC = () => {
  const { allFeatures } = useSelector((state: RootState) => state.features);
  const { selectedFeatures } = useBoundaryContext();
  const {
    layers: {
      shouldRenderFsaLayer,
      shouldRenderParcelLayer,
      shouldRenderParcelOwnerNames,
      shouldRenderStateLayer,
      shouldRenderCountyLayer,
      shouldRenderTownshipLayer,
      shouldRenderSectionLayer,
    },
    sources: {
      stateSourceData,
      countySourceData,
      townshipSourceData,
      sectionSourceData,
      parcelCentroidsSourceData,
      parcelsSourceData,
      cluSourceData,
    },
    isMapInstanceLoaded,
    mapInstance,
  } = useMapboxContext();

  const { is3dBuildingsEnabled } = useThreeDController();

  if (!mapInstance || !isMapInstanceLoaded) {
    return null;
  }

  return (
    <>
      {/* 3D Buildings */}
      <Source
        id={AcresMapboxSourceIds.BUILDINGS}
        type="vector"
        // Expects `maptilersdk.config.apiKey` to already be set
        url="https://api.maptiler.com/tiles/v3/tiles.json"
      >
        {is3dBuildingsEnabled && <Layer {...threeDBuildingsLayer} />}
      </Source>

      {shouldRenderParcelOwnerNames && parcelCentroidsSourceData && (
        <Source
          id={AcresMapboxSourceIds.PARCEL_CENTROIDS}
          {...parcelCentroidsSourceData}
        >
          <Layer {...visibleOwnerLabelsLayer} />
        </Source>
      )}
      {parcelsSourceData && (
        <Source {...parcelsSourceData} id={AcresMapboxSourceIds.PARCELS}>
          {shouldRenderParcelLayer && <Layer {...parcelFillLayer} />}
          {shouldRenderParcelLayer && <Layer {...parcelsOutline} />}
          {shouldRenderParcelLayer && <Layer {...parcelsOutlineHover} />}
        </Source>
      )}
      {shouldRenderFsaLayer && cluSourceData && (
        <Source {...cluSourceData} id={AcresMapboxSourceIds.CLU}>
          <Layer {...fsaFillLayer} />
          <Layer {...activeFsaLayer} />
          <Layer {...fsaLabelsLayer} />
        </Source>
      )}
      {stateSourceData && shouldRenderStateLayer && (
        <Source id={AcresMapboxSourceIds.STATES} {...stateSourceData}>
          <Layer {...stateFillLayer} />
          <Layer {...stateOutlineLayer} />
          <Layer {...stateHighlightLayer} />
          <Layer {...stateOutlinePermanentLayer} />
          <Layer {...stateLabelsLayer} />
        </Source>
      )}
      {countySourceData && shouldRenderCountyLayer && (
        <Source id={AcresMapboxSourceIds.COUNTIES} {...countySourceData}>
          <Layer {...countyFillLayer} />
          <Layer {...countyOutlineLayer} />
          <Layer {...countyLabelsLayer} />
        </Source>
      )}

      {shouldRenderTownshipLayer && townshipSourceData && (
        <Source
          id={AcresMapboxSourceIds.COUNTY_SUBDIVISIONS}
          {...townshipSourceData}
        >
          <Layer {...townshipFillLayer} />
          <Layer {...townshipLineLayer} />
          <Layer {...townshipLabelLayer} />
          <Layer {...townshipOutlineLayer} />
          <Layer {...townshipOutlinePermanentLayer} />
        </Source>
      )}

      {shouldRenderSectionLayer && sectionSourceData && (
        <Source id={AcresMapboxSourceIds.SECTIONS} {...sectionSourceData}>
          <Layer {...sectionsFillLayer} />
          <Layer {...sectionLineLayer} />
          <Layer {...sectionLabelLayer} />
          <Layer {...sectionOutlineLayer} />
          <Layer {...sectionOutlinePermanentLayer} />
        </Source>
      )}

      {/* Selection layers */}
      <Source
        data={{
          features: allFeatures.map((feature) => ({
            ...feature,
            id: isCompFeature(feature)
              ? feature.properties.mapbox_id
              : feature.id,
          })),
          type: 'FeatureCollection',
        }}
        id={selectionFillLayer.id}
        type="geojson"
      >
        <Layer {...selectionFillLayer} type="fill" />
        <Layer {...selectionLineLayer} type="line" />
        <Layer {...selectionDotLayer} type="circle" />
      </Source>

      {/* Boundaries */}
      <Source
        data={{
          features: selectedFeatures,
          type: 'FeatureCollection',
        }}
        id={boundaryOutlineLayer.id}
        type="geojson"
      >
        <Layer {...boundaryOutlineLayer} type="line" />
        <Layer {...boundaryFillLayer} type="fill" />
      </Source>
    </>
  );
};
